import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import GETJwtToken from "../../redux/authentication/authentication";
import {
  useCreateSimulationScenarioMutation,
  useLazyListSimulationAssetsQuery,
  useLazyListSimulationSandboxesQuery,
  useLazyListSimulationTargetsQuery,
  useUpdateSimulationScenarioMutation,
} from "../../redux/simulation/simulationAPI";
import { AdapterProtocol } from "../../flatbuffers/generated/archive/adapter-protocol";
import type { SimulationAssetT } from "../../flatbuffers/generated/api/simulation-asset";
import { SimulationBankSource } from "../../flatbuffers/generated/api/simulation-bank-source";
import type { SimulationCaptureReplayT } from "../../flatbuffers/generated/api/simulation-capture-replay";
import { SimulationOpcUaScriptT } from "../../flatbuffers/generated/api/simulation-opc-ua-script";
import { SimulationScenarioT } from "../../flatbuffers/generated/api/simulation-scenario";
import type { SimulationScenarioBankT } from "../../flatbuffers/generated/api/simulation-scenario-bank";
import type { SimulationTargetT } from "../../flatbuffers/generated/api/simulation-target";
import { formatUuidT } from "../../flatbuffers/display";
import { stringToUuidT, uuidsEqual } from "../../utils/uuid";
import {
  DEFAULT_SCRIPT_STEP_MS,
  autoScript,
  captureReplayFor,
  includeBank,
  opcUaDomains,
  patchBank,
  patchReplay,
  patchScenario,
  patchScript,
  replaceBank,
  sandboxReaches,
  scenarioProblems,
} from "./scenarioBuilder";

const text = (value: string | Uint8Array | null | undefined): string => String(value ?? "");

/** Separates an asset id from a flow's vendor address in a flow option's value. */
const FLOW_KEY_SEPARATOR = "|";

/** A change to one bank, as a function from the bank to its replacement. */
type BankChange = (bank: SimulationScenarioBankT) => SimulationScenarioBankT;

interface SimulationScenarioBuilderProps {
  /** The organization the scenario belongs to, as a dashed UUID string. */
  orgId: string;
  /** The scenario to edit; `null` creates a new one. A copy without an id is saved as new. */
  initial: SimulationScenarioT | null;
  open: boolean;
  onClose: () => void;
}

function newScenario(orgId: string): SimulationScenarioT {
  const scenario = new SimulationScenarioT();
  scenario.organizationId = stringToUuidT(orgId);
  return scenario;
}

interface BankSourceEditorProps {
  bank: SimulationScenarioBankT;
  target: SimulationTargetT;
  assets: readonly SimulationAssetT[];
  onChange: (change: BankChange) => void;
}

/** The settings of an OPC UA script source: its domain file and the generated writes. */
function ScriptEditor({ bank, target, assets, onChange }: BankSourceEditorProps) {
  const [t] = useTranslation("global");
  const [stepSeconds, setStepSeconds] = useState(DEFAULT_SCRIPT_STEP_MS / 1000);
  const script = bank.source as SimulationOpcUaScriptT;
  return (
    <Space direction="vertical" className="generalStyles__width100">
      <Select
        size="small"
        className="generalStyles__width100"
        placeholder={t("simulation.chooseDomain")}
        value={script.domainAssetId ? formatUuidT(script.domainAssetId) : undefined}
        options={opcUaDomains(assets).map((domain) => ({
          value: formatUuidT(domain.id),
          label: text(domain.name),
        }))}
        onChange={(id: string) =>
          onChange((current) => patchScript(current, { domainAssetId: stringToUuidT(id) }))
        }
      />
      <Space wrap>
        <Typography.Text type="secondary">
          {t("simulation.scriptSummary", { count: script.steps.length })}
        </Typography.Text>
        <InputNumber
          size="small"
          min={1}
          max={600}
          value={stepSeconds}
          addonAfter={t("simulation.secondsPerWrite")}
          onChange={(value) => setStepSeconds(Number(value ?? DEFAULT_SCRIPT_STEP_MS / 1000))}
        />
        <Button
          size="small"
          disabled={!script.domainAssetId || target.opcuaNodes.length === 0}
          onClick={() =>
            onChange((current) =>
              patchBank(current, {
                source: autoScript(target, script.domainAssetId, stepSeconds * 1000),
              }),
            )
          }
        >
          {t("simulation.autoScript")}
        </Button>
      </Space>
      {target.opcuaNodes.length === 0 && (
        <Typography.Text type="warning">{t("simulation.noMappedNodes")}</Typography.Text>
      )}
    </Space>
  );
}

/** The settings of a capture replay source: the captured endpoint and how to play it. */
function ReplayEditor({ bank, assets, onChange }: Omit<BankSourceEditorProps, "target">) {
  const [t] = useTranslation("global");
  const replay =
    bank.sourceType === SimulationBankSource.SimulationCaptureReplay
      ? (bank.source as SimulationCaptureReplayT | null)
      : null;
  const flowOptions = assets.flatMap((asset) =>
    asset.flows.map((flow) => ({
      value: `${formatUuidT(asset.id)}${FLOW_KEY_SEPARATOR}${text(flow.vendorAddress)}`,
      label: `${text(asset.name)} · ${text(flow.vendorAddress)}${
        flow.protocolDetected ? ` (${AdapterProtocol[flow.detectedProtocol]})` : ""
      }`,
    })),
  );

  const chooseFlow = (value: string) => {
    const [assetId, ...address] = value.split(FLOW_KEY_SEPARATOR);
    const asset = assets.find((candidate) => uuidsEqual(candidate.id, assetId));
    const flow = asset?.flows.find(
      (candidate) => text(candidate.vendorAddress) === address.join(FLOW_KEY_SEPARATOR),
    );
    if (!asset || !flow) return;
    const chosen = captureReplayFor({ asset, flow });
    // Keep how the bank was being played; only what it plays changes.
    onChange((current) =>
      patchReplay(
        current,
        replay
          ? { assetId: chosen.assetId, flowVendorAddress: chosen.flowVendorAddress }
          : {
              assetId: chosen.assetId,
              flowVendorAddress: chosen.flowVendorAddress,
              maxGapMs: chosen.maxGapMs,
            },
      ),
    );
  };

  return (
    <Space direction="vertical" className="generalStyles__width100">
      <Select
        size="small"
        showSearch
        optionFilterProp="label"
        className="generalStyles__width100"
        placeholder={t("simulation.chooseFlow")}
        value={
          replay?.assetId
            ? `${formatUuidT(replay.assetId)}${FLOW_KEY_SEPARATOR}${text(replay.flowVendorAddress)}`
            : undefined
        }
        options={flowOptions}
        onChange={chooseFlow}
      />
      {replay && (
        <Space wrap>
          <InputNumber
            size="small"
            min={0.1}
            max={20}
            step={0.5}
            value={replay.speed}
            addonBefore={t("simulation.speed")}
            addonAfter="×"
            onChange={(value) =>
              onChange((current) => patchReplay(current, { speed: Number(value ?? 1) }))
            }
          />
          <Space size="small">
            <Switch
              size="small"
              checked={replay.loop}
              onChange={(checked) => onChange((current) => patchReplay(current, { loop: checked }))}
            />
            {t("simulation.loop")}
          </Space>
          {bank.protocol === AdapterProtocol.MCE && (
            <Space size="small">
              <Switch
                size="small"
                checked={replay.startAtFirstData}
                onChange={(checked) =>
                  onChange((current) => patchReplay(current, { startAtFirstData: checked }))
                }
              />
              {t("simulation.startAtFirstData")}
            </Space>
          )}
        </Space>
      )}
    </Space>
  );
}

/** Where one bank's traffic comes from: a captured endpoint to replay, or an OPC UA script. */
function BankSourceEditor({ bank, target, assets, onChange }: BankSourceEditorProps) {
  return bank.sourceType === SimulationBankSource.SimulationOpcUaScript ? (
    <ScriptEditor bank={bank} target={target} assets={assets} onChange={onChange} />
  ) : (
    <ReplayEditor bank={bank} assets={assets} onChange={onChange} />
  );
}

/**
 * The scenario builder: name the scenario, pick the sandbox it runs through,
 * tick the organization's banks it drives and say where each bank's traffic
 * comes from. Banks start pre-matched (a capture recorded at the bank's own
 * address, or an auto script over the adapter's OPC UA values), so the common
 * case is a name, a sandbox and Save.
 */
function SimulationScenarioBuilder({
  orgId,
  initial,
  open,
  onClose,
}: SimulationScenarioBuilderProps) {
  const [t] = useTranslation("global");
  const [triggerTargets, { data: targets = [], isFetching: loadingTargets }] =
    useLazyListSimulationTargetsQuery();
  const [triggerSandboxes, { data: sandboxes = [] }] = useLazyListSimulationSandboxesQuery();
  const [triggerAssets, { data: assets = [] }] = useLazyListSimulationAssetsQuery();
  const [create, { isLoading: creating }] = useCreateSimulationScenarioMutation();
  const [update, { isLoading: updating }] = useUpdateSimulationScenarioMutation();
  const [draft, setDraft] = useState<SimulationScenarioT>(() => newScenario(orgId));

  useEffect(() => {
    if (!open) return;
    setDraft(initial ? initial.clone() : newScenario(orgId));
    (async () => {
      const token = await GETJwtToken();
      triggerTargets({ orgId, token });
      triggerSandboxes({ token });
      triggerAssets({ orgId, token });
    })();
  }, [open, initial, orgId, triggerTargets, triggerSandboxes, triggerAssets]);

  const changeBank = (target: SimulationTargetT, change: BankChange) =>
    setDraft((current) => replaceBank(current, target.bankId, change));

  const bankFor = (target: SimulationTargetT) =>
    draft.banks.find((bank) => uuidsEqual(bank.bankId, target.bankId));

  const orgSandboxes = useMemo(
    () => sandboxes.filter((sandbox) => uuidsEqual(sandbox.organizationId, orgId)),
    [sandboxes, orgId],
  );
  const chosenSandbox = orgSandboxes.find((sandbox) => text(sandbox.slug) === text(draft.sandbox));
  const problems = scenarioProblems(draft);

  const save = async () => {
    const token = await GETJwtToken();
    try {
      if (draft.id) {
        await update({ id: formatUuidT(draft.id), token, scenario: draft }).unwrap();
      } else {
        await create({ orgId, token, scenario: draft }).unwrap();
      }
      message.success(t("simulation.scenarioSaved", { name: text(draft.name) }));
      onClose();
    } catch (error) {
      const reason = (error as { data?: { message?: string } })?.data?.message;
      message.error(reason ?? t("simulation.scenarioSaveFailed"));
    }
  };

  const chooseSourceType = (target: SimulationTargetT, sourceType: SimulationBankSource) =>
    changeBank(target, (current) => {
      if (sourceType === SimulationBankSource.SimulationOpcUaScript) {
        const [domain] = opcUaDomains(assets);
        return patchBank(current, {
          sourceType,
          source: domain ? autoScript(target, domain.id) : new SimulationOpcUaScriptT(),
        });
      }
      return patchBank(current, { sourceType, source: null });
    });

  const columns: ColumnsType<SimulationTargetT> = [
    {
      title: t("simulation.include"),
      key: "include",
      width: 80,
      render: (_, target) => (
        <Checkbox
          checked={bankFor(target)?.enabled ?? false}
          onChange={(event) =>
            setDraft((current) => includeBank(current, target, assets, event.target.checked))
          }
        />
      ),
    },
    { title: t("simulation.bank"), key: "bank", render: (_, target) => text(target.bankName) },
    {
      title: t("simulation.protocol"),
      key: "protocol",
      render: (_, target) => <Tag>{AdapterProtocol[target.protocol]}</Tag>,
    },
    {
      title: t("simulation.vendorAddress"),
      key: "address",
      render: (_, target) => {
        const address = text(target.vendorAddress);
        if (!address) {
          return (
            <Typography.Text type="warning">{t("simulation.noVendorAddress")}</Typography.Text>
          );
        }
        return (
          <Space size="small" wrap>
            <code>{address}</code>
            {chosenSandbox && !sandboxReaches(chosenSandbox.vendorAddresses, address) && (
              <Tag color="warning">{t("simulation.notTunnelled")}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: t("simulation.source"),
      key: "source",
      render: (_, target) => {
        const bank = bankFor(target);
        if (!bank?.enabled) return null;
        return (
          <Space direction="vertical" className="generalStyles__width100">
            <Select
              size="small"
              value={bank.sourceType === SimulationBankSource.NONE ? undefined : bank.sourceType}
              options={[
                {
                  value: SimulationBankSource.SimulationCaptureReplay,
                  label: t("simulation.captureReplay"),
                },
                {
                  value: SimulationBankSource.SimulationOpcUaScript,
                  label: t("simulation.opcuaScript"),
                },
              ]}
              placeholder={t("simulation.chooseSource")}
              onChange={(sourceType: SimulationBankSource) => chooseSourceType(target, sourceType)}
            />
            <BankSourceEditor
              bank={bank}
              target={target}
              assets={assets}
              onChange={(change) => changeBank(target, change)}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <Drawer
      width={1100}
      open={open}
      onClose={onClose}
      title={draft.id ? t("simulation.editScenario") : t("simulation.newScenario")}
      extra={
        <Button
          type="primary"
          disabled={problems.length > 0}
          loading={creating || updating}
          onClick={save}
        >
          {t("simulation.save")}
        </Button>
      }
    >
      <Space direction="vertical" className="generalStyles__width100">
        <Input
          placeholder={t("simulation.scenarioName")}
          value={text(draft.name)}
          onChange={(event) =>
            setDraft((current) => patchScenario(current, { name: event.target.value }))
          }
        />
        <Input.TextArea
          rows={2}
          placeholder={t("simulation.scenarioDescription")}
          value={text(draft.description)}
          onChange={(event) =>
            setDraft((current) => patchScenario(current, { description: event.target.value }))
          }
        />
        <Select
          className="generalStyles__width100"
          placeholder={t("simulation.chooseSandbox")}
          value={text(draft.sandbox) || undefined}
          options={orgSandboxes.map((sandbox) => ({
            value: text(sandbox.slug),
            label: `${text(sandbox.slug)} · ${t("simulation.tunnelCount", {
              count: sandbox.vendorAddresses.length,
            })}`,
          }))}
          onChange={(slug: string) =>
            setDraft((current) => patchScenario(current, { sandbox: slug }))
          }
        />
        {orgSandboxes.length === 0 && (
          <Alert type="warning" showIcon message={t("simulation.noSandbox")} />
        )}
        <Table
          size="small"
          rowKey={(target) => formatUuidT(target.bankId)}
          columns={columns}
          dataSource={targets}
          loading={loadingTargets}
          pagination={false}
        />
        {problems.length > 0 && (
          <Alert
            type="info"
            showIcon
            message={t("simulation.beforeSaving")}
            description={
              <ul>
                {problems.map((problem) => (
                  <li key={`${problem.key}${"bank" in problem ? problem.bank : ""}`}>
                    {t(problem.key, "bank" in problem ? { name: problem.bank } : undefined)}
                  </li>
                ))}
              </ul>
            }
          />
        )}
      </Space>
    </Drawer>
  );
}

export default SimulationScenarioBuilder;
